import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Form, Badge, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { LuSearch, LuRefreshCw, LuCopy, LuArrowUpRight, LuPlus, LuUserPlus } from "react-icons/lu";
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getInvitations, resendInvitation, setFilter } from '../../redux/reducers/invitationSlice';
import SendInvitationModal from './SendInvitationModal';

const inviteStatusColors = {
  activated: { bg: '#d1fae5', color: '#10b981' },
  pending: { bg: '#fef3c7', color: '#f59e0b' },
  expired: { bg: '#fee2e2', color: '#ef4444' },
};

const getAvatarColor = (id = "") => {
  const colors = ["#28B0C4", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEEAD", "#D4A5A5", "#9B59B6"];
  const index = id ? id.toString().length % colors.length : 0;
  return colors[index];
};

const getInitials = (name = "") => {
  return name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "C";
};

const InvitationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invitations, loading, pagination, filters } = useSelector((state) => state.invitation);
  const [showSendModal, setShowSendModal] = useState(false);
  const businessId = localStorage.getItem('activeBusinessId');

  useEffect(() => {
    dispatch(getInvitations({ ...filters, businessId }));
  }, [dispatch, filters, businessId]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilter({ key, value }));
  };

  const handleResend = (id) => {
    dispatch(resendInvitation(id));
  };

  const handleCopyLink = (token) => {
    const link = `${window.location.origin}/join/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invitation link copied to clipboard");
  };

  const getStatus = (customer) => {
    const now = new Date();
    if (customer.portalAuth.isActivated) return 'activated';
    if (new Date(customer.portalAuth.inviteTokenExpiry) < now) return 'expired';
    return 'pending';
  };

  return (
    <div className="invitation-list-page p-4">
      {/* Stats Row */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm rounded-4 p-3">
            <div className="small text-muted fw-semibold">Total Sent</div>
            <div className="h4 fw-bold mb-0">{pagination?.total || 0}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm rounded-4 p-3">
            <div className="small text-muted fw-semibold">Activated</div>
            <div className="h4 fw-bold mb-0 text-success">
              {invitations.filter(i => i.portalAuth.isActivated).length}
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm rounded-4 p-3">
            <div className="small text-muted fw-semibold">Pending</div>
            <div className="h4 fw-bold mb-0 text-warning">
              {invitations.filter(i => !i.portalAuth.isActivated && new Date(i.portalAuth.inviteTokenExpiry) > new Date()).length}
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm rounded-4 p-3">
            <div className="small text-muted fw-semibold">Expired</div>
            <div className="h4 fw-bold mb-0 text-danger">
              {invitations.filter(i => !i.portalAuth.isActivated && new Date(i.portalAuth.inviteTokenExpiry) < new Date()).length}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filter Bar */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-3">
          <Row className="align-items-center">
            <Col md={4}>
              <div className="position-relative">
                <LuSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  type="text"
                  placeholder="Search name or email..."
                  className="ps-5 border-0 bg-light rounded-3"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </Col>
            <Col md={2}>
              <Form.Select
                className="border-0 bg-light rounded-3 fw-semibold"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="activated">Activated</option>
                <option value="expired">Expired</option>
              </Form.Select>
            </Col>
            <Col className="text-end">
              <Button 
                className="rounded-3 fw-semibold d-flex align-items-center gap-2 ms-auto"
                style={{ background: '#28B0C4', border: 'none', color: 'white' }}
                onClick={() => setShowSendModal(true)}
              >
                <LuUserPlus size={18} /> Send Invitation
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Table hover responsive className="mb-0">
          <thead className="bg-light">
            <tr>
              <th className="ps-4 py-3 border-0 small text-uppercase text-muted fw-bold">Customer</th>
              <th className="py-3 border-0 small text-uppercase text-muted fw-bold">Email</th>
              <th className="py-3 border-0 small text-uppercase text-muted fw-bold">Invited</th>
              <th className="py-3 border-0 small text-uppercase text-muted fw-bold">Status</th>
              <th className="py-3 border-0 small text-uppercase text-muted fw-bold">Expires</th>
              <th className="pe-4 py-3 border-0 text-end small text-uppercase text-muted fw-bold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  <Spinner animation="border" variant="primary" size="sm" />
                </td>
              </tr>
            ) : invitations.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-5 text-muted">No invitations found.</td>
              </tr>
            ) : (
              invitations.map((customer) => {
                const inviteStatus = getStatus(customer);
                return (
                  <tr key={customer._id} className="align-middle">
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                          style={{ width: 32, height: 32, fontSize: '11px', backgroundColor: getAvatarColor(customer._id) }}>
                          {getInitials(customer.name || customer.email)}
                        </div>
                        <div>
                          <div className="fw-bold small">
                            {customer.name || <span className="text-muted fst-italic">Profile not completed</span>}
                          </div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>
                            {customer.portalAuth.inviteResendCount > 0
                              ? `Resent ${customer.portalAuth.inviteResendCount}×`
                              : 'First invitation'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="small">{customer.email}</td>
                    <td className="small">{moment(customer.portalAuth.invitedAt).format('MMM D, YYYY')}</td>
                    <td>
                      <span className="rounded-pill px-2 py-1 fw-bold"
                        style={{ fontSize: '10px', backgroundColor: inviteStatusColors[inviteStatus].bg, color: inviteStatusColors[inviteStatus].color }}>
                        {inviteStatus.toUpperCase()}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {inviteStatus === 'activated' ? '—' : 
                       inviteStatus === 'expired' ? 'Expired' : 
                       moment(customer.portalAuth.inviteTokenExpiry).format('MMM D')}
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        {inviteStatus !== 'activated' && (
                          <Button variant="light" size="sm" className="rounded-3 fw-semibold border"
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                            onClick={() => handleResend(customer._id)}>
                            <LuRefreshCw size={12} className="me-1" /> Resend
                          </Button>
                        )}
                        {inviteStatus === 'pending' && (
                          <Button variant="light" size="sm" className="rounded-3 fw-semibold border"
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                            onClick={() => handleCopyLink(customer.portalAuth.inviteToken)}>
                            <LuCopy size={12} className="me-1" /> Copy
                          </Button>
                        )}
                        {inviteStatus === 'activated' && (
                          <Button variant="light" size="sm" className="rounded-circle p-2 border"
                            onClick={() => navigate(`/customer-detail/${customer._id}`)}>
                            <LuArrowUpRight size={15} className="text-muted" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        {/* Pagination placeholder */}
        {pagination?.pages > 1 && (
          <div className="p-3 border-top d-flex justify-content-between align-items-center">
            <small className="text-muted">Showing {invitations.length} of {pagination.total} invitations</small>
            <div className="d-flex gap-2">
              <Button 
                variant="light" 
                size="sm" 
                disabled={pagination.page === 1}
                onClick={() => handleFilterChange('page', pagination.page - 1)}
              >Previous</Button>
              <Button 
                variant="light" 
                size="sm" 
                disabled={pagination.page === pagination.pages}
                onClick={() => handleFilterChange('page', pagination.page + 1)}
              >Next</Button>
            </div>
          </div>
        )}
      </Card>

      <SendInvitationModal show={showSendModal} onHide={() => setShowSendModal(false)} />
    </div>
  );
};

export default InvitationList;
